import React from 'react';
import { Box, Stack, Typography, Divider, alpha, useTheme } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Handshake as HandshakeIcon,
  LockPerson as LockPersonIcon,
  VolunteerActivism as DonationIcon,
  Block as BlockIcon,
  WarningAmber as WarningAmberIcon,
  Gavel as GavelIcon,
  Public as PublicIcon,
  ContactSupport as ContactSupportIcon,
} from '@mui/icons-material';

const TermsContent = () => {
  const theme = useTheme();

  const sections = [
    {
      icon: <CheckCircleIcon />,
      title: "Acceptance of Terms",
      content:
        "By accessing and using the Mindsinaction website (mindsinaction.com.na), you accept and agree to be bound by these Terms and Conditions and our Privacy Policy. These terms apply to all visitors, donors, partners, and users of our platform. If you do not agree, please discontinue use of this website immediately.",
    },
    {
      icon: <HandshakeIcon />,
      title: "Use License",
      content:
        "Permission is granted to temporarily access the materials on this website for personal, non-commercial viewing only. You may not reproduce, distribute, modify, or create derivative works from any content without prior written consent from Mindsinaction. All educational content, project materials, and media remain the intellectual property of Mindsinaction.",
    },
    {
      icon: <LockPersonIcon />,
      title: "User Registration & Privacy",
      content:
        "Certain features of this website may require account registration. You agree to provide accurate, current, and complete information and to keep it updated. Mindsinaction is committed to protecting your personal data in accordance with the laws of the Republic of Namibia. We do not sell or share your personal information with third parties without your consent, except as required by law.",
    },
    {
      icon: <DonationIcon />,
      title: "Payments & Donations",
      content:
        "All donations and payments made through this platform support Mindsinaction's STEAM education programmes in Namibia. Donations are processed through secure payment gateways and are non-refundable unless a processing error has occurred. By donating, you confirm that you are the authorised account holder and that your contribution is made voluntarily in support of our mission.",
    },
    {
      icon: <BlockIcon />,
      title: "Prohibited Activities",
      content:
        "Users of this website agree not to:\n• Violate any applicable local, national, or international law\n• Impersonate Mindsinaction staff, partners, or other users\n• Upload or transmit malicious code, spam, or harmful content\n• Attempt unauthorised access to any part of our systems\n• Use this platform to harass, exploit, or harm minors\n• Misrepresent your affiliation with Mindsinaction or its programmes",
    },
    {
      icon: <WarningAmberIcon />,
      title: "Disclaimer of Warranties",
      content:
        "All materials and content on this website are provided on an \"as is\" basis. Mindsinaction makes no warranties, expressed or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement. We do not guarantee uninterrupted or error-free access to the website.",
    },
    {
      icon: <GavelIcon />,
      title: "Limitation of Liability",
      content:
        "Mindsinaction shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of, or inability to use, this website or its content. Our total liability in any matter arising from these terms shall not exceed the amount you paid, if any, to access our services.",
    },
    {
      icon: <PublicIcon />,
      title: "Governing Law",
      content:
        "These Terms and Conditions are governed by and construed in accordance with the laws of the Republic of Namibia. Any disputes arising from or relating to these terms shall be subject to the exclusive jurisdiction of the courts of Namibia.",
    },
    {
      icon: <ContactSupportIcon />,
      title: "Contact Information",
      content:
        "For any questions regarding these Terms and Conditions, please reach out to us:\n\nEmail: admin@mindsinaction.com.na\nWebsite: mindsinaction.com.na\nLocation: Windhoek, Namibia",
    },
  ];

  return (
    <Box>
      {sections.map((section, index) => (
        <Box key={index} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '8px',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                flexShrink: 0,
                '& svg': { fontSize: 18 },
              }}
            >
              {section.icon}
            </Box>
            <Typography
              variant="subtitle1"
              component="h3"
              fontWeight="bold"
              sx={{ fontSize: '0.95rem', color: 'text.primary' }}
            >
              {index + 1}. {section.title}
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 6.5, whiteSpace: 'pre-line', lineHeight: 1.75 }}
          >
            {section.content}
          </Typography>

          {index < sections.length - 1 && (
            <Divider sx={{ mt: 2.5, borderColor: alpha(theme.palette.divider, 0.6) }} />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default TermsContent;